import { reportError } from 'utils/error'

const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_STALL_TIMEOUT_MS = 30_000
const DEFAULT_OVERALL_TIMEOUT_MS = 30 * 60 * 1000

export type XhrUploadOptions = {
  /** A static URL or a factory called before each attempt so retries can fetch a fresh signed URL. */
  url: string | (() => Promise<string>)
  body: Blob | File
  contentType?: string
  onProgress?: (pct: number) => void
  onRetry?: (attempt: number) => void
  signal?: AbortSignal
  maxAttempts?: number
  /** Abort the request if no `progress` event fires within this many ms. */
  stallTimeoutMs?: number
  /** Wall-clock ceiling per attempt. */
  overallTimeoutMs?: number
  /** Sentry tags applied to the failure report — typically workspace_id, file_size_mb, mime_type. */
  errorTags?: Record<string, string | number | undefined>
}

export type XhrUploadResult = { url: string }

/**
 * PUT a body to a URL with progress, retry, and stall detection.
 *
 * Why this exists vs `fetch`: we need upload-progress events (fetch doesn't
 * surface them) and a stall detector — a no-progress watchdog that aborts
 * when the connection half-opens. Without that, a stalled XHR sits in-flight
 * forever and the caller's promise never settles.
 *
 * Each retry uses a fresh `XMLHttpRequest` because reopening an instance
 * that just fired `error` is undefined behavior.
 */
export async function xhrUpload(options: XhrUploadOptions): Promise<XhrUploadResult> {
  const {
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
    overallTimeoutMs = DEFAULT_OVERALL_TIMEOUT_MS,
    onRetry,
    signal,
    errorTags,
    url: urlOrFactory,
    ...attemptOptions
  } = options

  let lastError: Error | null = null

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError')
    if (attempt > 1) onRetry?.(attempt)

    try {
      const url = typeof urlOrFactory === 'function' ? await urlOrFactory() : urlOrFactory
      return await attemptUpload({ ...attemptOptions, url, signal, stallTimeoutMs, overallTimeoutMs })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err
      lastError = err as Error
      // S3 responses like 403 (signed URL expired) or 404 are permanent — retrying won't help.
      if ((err as Error & { noRetry?: boolean }).noRetry) break
    }
  }

  const finalError = lastError ?? new Error('Upload failed')
  reportError(finalError, { severity: 'error' }, { attempts: maxAttempts, ...errorTags })
  throw finalError
}

type AttemptOptions = Pick<XhrUploadOptions, 'body' | 'contentType' | 'onProgress' | 'signal'> & {
  url: string
  stallTimeoutMs: number
  overallTimeoutMs: number
}

function attemptUpload(opts: AttemptOptions): Promise<XhrUploadResult> {
  return new Promise<XhrUploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let stallTimer: ReturnType<typeof setTimeout> | null = null
    let overallTimer: ReturnType<typeof setTimeout> | null = null
    let settled = false

    const cleanup = () => {
      if (stallTimer) clearTimeout(stallTimer)
      if (overallTimer) clearTimeout(overallTimer)
      opts.signal?.removeEventListener('abort', onAbortSignal)
    }

    const settle = (fn: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      fn()
    }

    const onAbortSignal = () =>
      settle(() => {
        xhr.abort()
        reject(new DOMException('Upload aborted', 'AbortError'))
      })

    const resetStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        settle(() => {
          xhr.abort()
          reject(new Error(`Upload stalled (no progress for ${opts.stallTimeoutMs}ms)`))
        })
      }, opts.stallTimeoutMs)
    }

    xhr.upload?.addEventListener('progress', (event) => {
      resetStallTimer()
      if (event.lengthComputable && opts.onProgress) {
        opts.onProgress((event.loaded / event.total) * 100)
      }
    })

    xhr.upload?.addEventListener('error', () => settle(() => reject(new Error('Upload failed (network error)'))))

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        settle(() => resolve({ url: opts.url }))
      } else {
        const err: Error & { noRetry?: boolean } = new Error(`Upload failed (HTTP ${xhr.status})`)
        // 5xx is potentially transient; 4xx is permanent (expired signed URL, bad request, etc).
        if (xhr.status >= 400 && xhr.status < 500) err.noRetry = true
        settle(() => reject(err))
      }
    })

    if (opts.signal) {
      if (opts.signal.aborted) {
        settle(() => reject(new DOMException('Upload aborted', 'AbortError')))
        return
      }
      opts.signal.addEventListener('abort', onAbortSignal)
    }

    overallTimer = setTimeout(() => {
      settle(() => {
        xhr.abort()
        reject(new Error(`Upload exceeded overall timeout of ${opts.overallTimeoutMs}ms`))
      })
    }, opts.overallTimeoutMs)

    xhr.open('PUT', opts.url, true)
    if (opts.contentType) xhr.setRequestHeader('Content-Type', opts.contentType)
    resetStallTimer()
    xhr.send(opts.body)
  })
}
